import { useMemo } from 'react';
import { Icon, Heading, Muted } from '@/ui';
import { PermissionsContext, usePermissions, type PermissionsApi } from './permissions-context';

export function PermissionsProvider({
  permissions,
  children,
}: {
  permissions: string[];
  children: React.ReactNode;
}) {
  const value = useMemo<PermissionsApi>(
    () => ({
      permissions,
      has: (p) => permissions.includes(p),
      hasAny: (list) => list.length === 0 || list.some((p) => permissions.includes(p)),
    }),
    [permissions],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

/**
 * Route guard. Renders the screen when the user holds any of the listed
 * permissions, and a plain explanation when they do not.
 *
 * The denial names the missing capability rather than saying "Access Denied" —
 * a clerk who cannot open Collection needs to know what to ask their
 * administrator for, not that a door is shut.
 */
export function RequirePermission({
  permissions,
  children,
}: {
  permissions: string[];
  children: React.ReactNode;
}) {
  const { hasAny } = usePermissions();
  if (hasAny(permissions)) return <>{children}</>;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
      <Icon name="lock" size={22} className="text-faint" />
      <Heading level={2}>You do not have access to this screen</Heading>
      <Muted>
        Ask an administrator for the{' '}
        <span className="font-mono text-ink">{permissions.join(' or ')}</span> permission.
      </Muted>
    </div>
  );
}
